import type { CirclesChart } from '../../circlesChart.js';
import type { CirclesReplayInputs, ReplayOutcome } from '../../replay.js';
import { computeAccuracy } from '../../scoring.js';
import {
  buildCirclesEngineConfig,
  step,
  type CirclesEngineEvent,
  type CirclesEngineInput,
  type CirclesSpinSample,
} from './core.js';
import {
  createCirclesInitialState,
  resetCirclesEngineStateForNextLoop,
} from './state.js';

export interface CirclesRunReplayOptions {
  stepBeats?: number;
  collectEvents?: boolean;
}

export interface CirclesRunReplayResult {
  outcome: ReplayOutcome;
  events: CirclesEngineEvent[];
  /** Number of full chart loops the player cleared. 0 if they died in loop 1. */
  loopsCompleted: number;
}

const DEFAULT_STEP_BEATS = 1 / 16;

/**
 * Replay a captured Circles run through the deterministic engine.
 *
 * Walks `replay.loops` in order, simulating one chart traversal per
 * loop and resetting chart-relative engine state between loops via
 * {@link resetCirclesEngineStateForNextLoop}. Mirror of the Pulse
 * runner — see that doc-comment for the multi-loop rationale.
 */
export function runCirclesReplay(
  chart: CirclesChart,
  replay: CirclesReplayInputs,
  opts: CirclesRunReplayOptions = {},
): CirclesRunReplayResult {
  const config = buildCirclesEngineConfig(chart);
  const state = createCirclesInitialState(chart);
  const stepBeats = opts.stepBeats ?? DEFAULT_STEP_BEATS;
  const collectEvents = opts.collectEvents !== false;
  const allEvents: CirclesEngineEvent[] = [];

  let loopsCompleted = 0;
  for (let loopIdx = 0; loopIdx < replay.loops.length; loopIdx += 1) {
    if (loopIdx > 0) {
      resetCirclesEngineStateForNextLoop(state);
    }
    const inputs: CirclesEngineInput[] = replay.loops[loopIdx].inputs.map((i) => ({
      beat: i.beat,
      x: i.x,
      y: i.y,
    }));
    // Pre-sort spin samples once per loop so the per-tick scan can
    // walk a forward cursor; samples landing exactly at a tick
    // boundary are flushed with that tick (same `<=` rule as taps).
    // `pressing` MUST be carried through — it gates slider tracking
    // (`processSliderSample` reads it) and dropping it would make
    // every server-side replay treat samples as "not holding the
    // button", crediting zero ticks/repeats/tails and rejecting the
    // run as `score_mismatch` against the client's score.
    const spinSamples: CirclesSpinSample[] = (replay.loops[loopIdx].spinSamples ?? [])
      .map((s) => ({ beat: s.beat, x: s.x, y: s.y, pressing: s.pressing }))
      .sort((a, b) => a.beat - b.beat);
    // The live client's audio clock runs slightly past the chart's final
    // beat, so a late tap on the last circle (still inside its OK window) is
    // captured at beat > totalBeats and scored by the client. `step()`
    // honours such taps (it processes merged events before firing the
    // end-of-chart completion), so the runner MUST deliver them too — else the
    // final object auto-misses on the server, the combo breaks, and the run is
    // rejected as `replay_mismatch`. Extend the simulation end to cover any
    // trailing input/sample beyond totalBeats; inputs/samples are pre-sorted
    // so the last element is the max beat.
    const lastInputBeat = inputs.length > 0 ? inputs[inputs.length - 1].beat : 0;
    const lastSampleBeat =
      spinSamples.length > 0 ? spinSamples[spinSamples.length - 1].beat : 0;
    const endBeat = Math.max(chart.totalBeats, lastInputBeat, lastSampleBeat);
    let cursor = 0;
    let nextInputIdx = 0;
    let nextSpinIdx = 0;

    while (cursor < endBeat && state.alive && !state.completed) {
      // Fixed 1/16-beat ticks up to totalBeats; the tick that reaches the
      // chart end extends to `endBeat` so any trailing input/sample lands in
      // the same step that crosses totalBeats and completes the chart
      // (mirroring the client's final frame, which processes the late tap
      // before completion). `step()` resolves merged inputs first, so the
      // late tap is judged, then the end-of-chart sweep fires.
      const rawTickEnd = cursor + stepBeats;
      const tickEnd = rawTickEnd >= chart.totalBeats ? endBeat : rawTickEnd;
      const tickInputs: CirclesEngineInput[] = [];
      while (
        nextInputIdx < inputs.length &&
        inputs[nextInputIdx].beat <= tickEnd
      ) {
        tickInputs.push(inputs[nextInputIdx]);
        nextInputIdx += 1;
      }
      const tickSpinSamples: CirclesSpinSample[] = [];
      while (
        nextSpinIdx < spinSamples.length &&
        spinSamples[nextSpinIdx].beat <= tickEnd
      ) {
        tickSpinSamples.push(spinSamples[nextSpinIdx]);
        nextSpinIdx += 1;
      }

      const out = step({
        config,
        state,
        fromBeat: cursor,
        toBeat: tickEnd,
        inputs: tickInputs,
        spinSamples: tickSpinSamples,
      });
      if (collectEvents) {
        allEvents.push(...out.events);
      }
      cursor = tickEnd;
    }

    if (state.completed) {
      loopsCompleted += 1;
    }
    if (!state.alive) {
      break;
    }
  }

  const outcome: ReplayOutcome = {
    completed: loopsCompleted > 0,
    score: state.score,
    combo: state.combo,
    maxCombo: state.maxCombo,
    perfects: state.perfects,
    goods: state.goods,
    oks: state.oks,
    misses: state.misses,
    accuracy: computeAccuracy(state.perfects, state.goods, state.oks, state.misses),
    deathBeat: state.deathBeat,
    deathReason: state.deathReason ?? undefined,
  };

  return { outcome, events: allEvents, loopsCompleted };
}

/**
 * Build the deterministic "perfect" replay for a Circles chart: a tap
 * exactly on every circle's beat at its target position. Spinners
 * auto-resolve to a free `good`, so they're not in the input list.
 *
 * Used by builtin-map smoke tests (every chart must complete with this
 * payload) and the future authoring/editor preview's "play perfect" UI.
 */
export function buildPerfectCirclesReplayInputs(chart: CirclesChart): CirclesEngineInput[] {
  return chart.hitObjects
    .filter((h) => h.kind !== 'spinner')
    .map((h) => ({ beat: h.beat, x: h.x, y: h.y }))
    .sort((a, b) => a.beat - b.beat);
}
